/* Inline icon set for the public BatchBooks pages.

   The prototypes pull glyphs from the Material Symbols webfont over a Google
   Fonts <link>. Rendering an icon font just for the marketing and auth screens
   means a blocking third-party request and a flash of unstyled glyph names, so
   the handful of symbols actually used are redrawn here as inline SVG.

   Icons are decorative: every one is aria-hidden and each is paired with real
   text in the markup. */

export type BbIconName =
  | 'chart'
  | 'mail'
  | 'lock'
  | 'eye'
  | 'eye-off'
  | 'arrow-right'
  | 'upload'
  | 'download'
  | 'trash'
  | 'shield-check'
  | 'shield'
  | 'bolt'
  | 'card-off'
  | 'check'
  | 'check-circle'
  | 'x-circle'
  | 'warning'
  | 'rule'
  | 'edit'
  | 'done-all'
  | 'scanner'
  | 'inbox'
  | 'folder'
  | 'history'
  | 'undo'
  | 'plus'
  | 'chevron-down'
  | 'settings'
  | 'tune'
  | 'logout'
  | 'edit-note'
  | 'bank'
  | 'credit-card'
  | 'users'
  | 'list'
  | 'dashboard'
  | 'wallet'
  | 'box'
  | 'store'
  | 'category'
  | 'badge'
  | 'pin'
  | 'coins'
  | 'menu'
  | 'receipt'
  | 'cart'
  | 'timer'
  | 'book'
  | 'cloud-upload'
  | 'sheet'
  | 'drive'
  | 'save'
  | 'sync'
  | 'layers-clear'
  | 'arrow-left'
  | 'play'
  | 'package'
  | 'search'
  | 'info'
  | 'filter'
  | 'calendar'
  | 'file-text'
  | 'building'

const PATHS: Record<BbIconName, string[]> = {
  chart: ['M3 3v18h18', 'm7 14 3-4 3 3 4-6'],
  mail: ['M3 6h18v12H3z', 'm3 7 9 6 9-6'],
  lock: ['M6 10h12v10H6z', 'M9 10V7a3 3 0 0 1 6 0v3'],
  eye: ['M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z'],
  'eye-off': [
    'm3 3 18 18',
    'M10.6 6.2A10.6 10.6 0 0 1 12 6c6.5 0 10 6 10 6a17.6 17.6 0 0 1-3.4 4',
    'M6.5 8.5C3.9 10.2 2 12 2 12s3.5 6 10 6a11 11 0 0 0 4-.7',
    'M9.9 9.9a3 3 0 0 0 4.2 4.2',
  ],
  'arrow-right': ['M4 12h16', 'm13 5 7 7-7 7'],
  upload: ['M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z', 'M14 3v5h5', 'M12 18v-6', 'm9.5 14.5 2.5-2.5 2.5 2.5'],
  download: ['M12 3v12', 'm7 12 5 5 5-5', 'M4 20h16'],
  trash: ['M4 7h16', 'M6 7v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7', 'M9 7V4h6v3', 'M10 11v6', 'M14 11v6'],
  'shield-check': ['M12 3l8 3v6c0 4.5-3.2 7.9-8 9-4.8-1.1-8-4.5-8-9V6Z', 'm9 12 2 2 4-4'],
  shield: ['M12 3l8 3v6c0 4.5-3.2 7.9-8 9-4.8-1.1-8-4.5-8-9V6Z'],
  bolt: ['M13 2 4 14h7l-1 8 9-12h-7Z'],
  'card-off': ['M3 6h18v12H3z', 'M3 10h18', 'm3 3 18 18'],
  check: ['m5 13 4 4 10-10'],
  'check-circle': ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'm8.5 12 2.5 2.5 4.5-5'],
  'x-circle': ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'm9 9 6 6', 'm15 9-6 6'],
  warning: ['M12 4 2.5 20h19Z', 'M12 10v4', 'M12 17h.01'],
  rule: ['m3 8 3 3 5-5', 'M13 10h8', 'M3 17h8', 'm15 15 4 4', 'm19 15-4 4'],
  edit: ['M12 20h9', 'M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z'],
  'done-all': ['m2 13 4 4 5-5', 'm10 13 4 4 8-9'],
  scanner: ['M4 8V6a2 2 0 0 1 2-2h2', 'M16 4h2a2 2 0 0 1 2 2v2', 'M20 16v2a2 2 0 0 1-2 2h-2', 'M8 20H6a2 2 0 0 1-2-2v-2', 'M7 12h10'],
  inbox: ['M3 12 5 5h14l2 7v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Z', 'M3 12h5l1.5 3h5L16 12h5'],
  folder: ['M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z'],
  history: ['M3.5 9A9 9 0 1 1 3 12', 'M3 4v5h5', 'M12 8v4.5l3 1.8'],
  undo: ['M9 14 4 9l5-5', 'M4 9h10a6 6 0 0 1 0 12h-3'],
  plus: ['M12 5v14', 'M5 12h14'],
  'chevron-down': ['m6 9 6 6 6-6'],
  settings: ['M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z', 'M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2 2 2 0 1 1-4 0 1.7 1.7 0 0 0-2.9-1.2l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 3 15a2 2 0 1 1 0-4 1.7 1.7 0 0 0 1.2-2.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 10 4.1a2 2 0 1 1 4 0 1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1A1.7 1.7 0 0 0 21 11a2 2 0 1 1 0 4 1.7 1.7 0 0 0-1.6 1Z'],
  tune: ['M4 8h10', 'M18 8h2', 'M4 16h4', 'M12 16h8', 'M16 6v4', 'M10 14v4'],
  logout: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'm16 17 5-5-5-5', 'M21 12H9'],
  'edit-note': ['M4 6h12', 'M4 11h8', 'M4 16h5', 'M19 12.5a1.9 1.9 0 0 1 2.7 2.7L17 20l-3 .8.8-3Z'],
  bank: ['m3 10 9-6 9 6', 'M4 10v8', 'M9 10v8', 'M15 10v8', 'M20 10v8', 'M3 21h18'],
  'credit-card': ['M3 6h18v12H3z', 'M3 10h18', 'M7 15h3'],
  users: ['M16 20v-1.5a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4V20', 'M9.5 10.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z', 'M21 20v-1.5a4 4 0 0 0-3-3.9', 'M16 3.6a4 4 0 0 1 0 7.4'],
  list: ['M8 6h13', 'M8 12h13', 'M8 18h13', 'M3.5 6h.01', 'M3.5 12h.01', 'M3.5 18h.01'],
  dashboard: ['M3 4h7v7H3z', 'M14 4h7v4h-7z', 'M14 12h7v8h-7z', 'M3 15h7v5H3z'],
  wallet: ['M3 7a2 2 0 0 1 2-2h12v4', 'M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9H5a2 2 0 0 1-2-2Z', 'M16 13h2'],
  box: ['m12 3 8 4.5v9L12 21l-8-4.5v-9Z', 'm4 7.5 8 4.5 8-4.5', 'M12 12v9'],
  store: ['M4 9h16v10a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Z', 'm4 9 1.5-5h13L20 9', 'M9 20v-6h6v6'],
  category: ['m12 3 3.5 6h-7Z', 'M4.5 13h6v6h-6z', 'M17 19.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z'],
  badge: ['M4 5h16v14H4z', 'M9.5 11.5a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6Z', 'M6.5 16a3.5 3.5 0 0 1 6 0', 'M15 10h3', 'M15 14h3'],
  pin: ['M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z', 'M12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z'],
  coins: ['M9 13a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z', 'M13.5 6.2A5 5 0 1 1 15 16', 'M3 17c0 1.7 2.7 3 6 3s6-1.3 6-3'],
  menu: ['M4 7h16', 'M4 12h16', 'M4 17h16'],
  receipt: ['M5 3v18l2.5-1.6L10 21l2-1.6L14 21l2.5-1.6L19 21V3Z', 'M9 8h6', 'M9 12h6'],
  cart: ['M3 4h2l2.2 10.4a2 2 0 0 0 2 1.6h7.6a2 2 0 0 0 2-1.6L20 7H6', 'M9.5 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z', 'M17.5 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z'],
  timer: ['M12 21a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z', 'M12 9v4l2.5 2', 'M9 2h6'],
  book: ['M4 4.5A2.5 2.5 0 0 1 6.5 2H20v16H6.5A2.5 2.5 0 0 0 4 20.5Z', 'M4 20.5A2.5 2.5 0 0 1 6.5 18H20v4H6.5A2.5 2.5 0 0 1 4 20.5Z'],
  'cloud-upload': ['M7 18a4 4 0 0 1-.6-7.95A6 6 0 0 1 17.7 9.2 3.9 3.9 0 0 1 17 18', 'M12 21V11', 'm8.5 14.5 3.5-3.5 3.5 3.5'],
  sheet: ['M3 4h18v16H3z', 'M3 9h18', 'M3 14.5h18', 'M9 9v11', 'M15 9v11'],
  drive: ['m8.5 3 7 12', 'M2.5 18 8.5 6', 'M2.5 18h13', 'm15.5 15 3.5 6H8'],
  save: ['M5 3h11l3 3v15H5z', 'M8 3v6h7V3', 'M8 15h8'],
  sync: ['M20 11.5A8 8 0 0 0 6 6.5L4 8.5', 'M4 4v5h5', 'M4 12.5a8 8 0 0 0 14 5l2-2', 'M20 20v-5h-5'],
  'layers-clear': ['m3 3 18 18', 'm12 3 9 5-3.2 1.8', 'M7.6 8.4 3 11l9 5 3.3-1.8', 'M3 16.5 12 21l4-2.2'],
  'arrow-left': ['M20 12H4', 'm11 5-7 7 7 7'],
  play: ['M7 4.5v15l13-7.5Z'],
  package: ['M4 7.5 12 3l8 4.5v9L12 21l-8-4.5Z', 'M4 7.5 12 12l8-4.5', 'M12 12v9', 'M8 5.2l8 4.6'],
  search: ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z', 'm16.2 16.2 4.3 4.3'],
  info: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M12 11v5', 'M12 7.5h.01'],
  filter: ['M3 6h18', 'M7 12h10', 'M10 18h4'],
  calendar: ['M4 6h16v15H4z', 'M4 10h16', 'M8 3v4', 'M16 3v4'],
  'file-text': ['M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z', 'M14 3v5h5', 'M9 13h6', 'M9 17h4'],
  building: ['M4 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16', 'M16 9h2a2 2 0 0 1 2 2v10', 'M3 21h18', 'M8 7h4', 'M8 11h4', 'M8 15h4'],
}

export function BbIcon({ name, className = 'bb-icon' }: { name: BbIconName; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  )
}
